import { useOneClickTradingV2 } from '@ui/features/OneClickTrading/hooks/useOneClickTradingV2';
import { useClosingTradesStore } from '@ui/features/Websocket/useClosingTradesStore';
import { useDataChainId } from '@ui/hooks/datachain';
import { useMutationToast } from '@ui/hooks/useMutationToast';
import { useTxnWriteContract } from '@ui/hooks/useTxnWriteContract';
import { pollV2IntentStatus } from '@ui/services/backend/oneClickV2Service';
import TradingPortalFacet from '@ui/services/contract/abis/TradingPortalFacet';
import { getContractAddress } from '@ui/services/contract/constants';
import type { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import type { Address } from '@ui/types/alias';
import { extractErrorMessage } from '@ui/utils/extractContractError';
import { useCallback, useMemo, useState } from 'react';

import { useInvalidatePositionQuery } from '../../hooks/useInvalidateQuery';
import { useClosingPositionsStore } from '../../store/useClosingPositionsStore';

export const useCloseTrade = ({
  position,
  closeQty,
  broker = 0,
  onSuccess,
}: {
  position: Position;
  closeQty?: bigint;
  broker?: number;
  onSuccess?: () => void;
}) => {
  const invalidatePositionQuery = useInvalidatePositionQuery();
  const addClosingPosition = useClosingPositionsStore((s) => s.addClosingPosition);
  const addClosingTrade = useClosingTradesStore((s) => s.addClosingTrade);
  const mutationToastInfo = useMutationToast();

  const {
    isReady: canUseOneClickTradingV2,
    isSubmittingClose: isSubmittingCloseV2,
    isSubmittingPartialClose: isSubmittingPartialCloseV2,
    sendClosePosition: sendClosePositionV2,
    sendPartialClose: sendPartialCloseV2,
  } = useOneClickTradingV2();
  const [isAwaitingOneClickCloseResult, setIsAwaitingOneClickCloseResult] =
    useState(false);

  const { writeContract: writeContractOnchain, isPending } = useTxnWriteContract({
    onSuccess: () => {
      if (!closeQty) {
        addClosingTrade(position.id, chainId, position.timestamp);
        addClosingPosition(position.id, position.timestamp);
      }
      invalidatePositionQuery();

      onSuccess?.();
    },
  });
  const chainId = useDataChainId();

  const handleOneClickCloseV2 = useCallback(
    async (tradeHash: Address) => {
      if (!canUseOneClickTradingV2) return;

      setIsAwaitingOneClickCloseResult(true);
      mutationToastInfo.onMutate({
        title: 'In Progress',
        description: '⚡️ One-click V2 close in progress',
      });

      try {
        const intentHash = await sendClosePositionV2({
          positionHash: tradeHash,
        });

        const tradingStatus = await pollV2IntentStatus(intentHash);
        if (!tradingStatus.success) {
          throw new Error(
            tradingStatus.reason || 'One-click V2 close execution failed',
          );
        }

        const txHash = tradingStatus.txnHash as Address;

        if (!closeQty) {
          addClosingTrade(position.id, chainId, position.timestamp);
          addClosingPosition(position.id, position.timestamp);
        }
        invalidatePositionQuery();

        mutationToastInfo.onSuccess({
          title: '⚡️ Completed',
          description: 'Position closed successfully via one-click V2',
          txHash,
        });

        onSuccess?.();

        return txHash;
      } catch (error) {
        const message =
          error instanceof Error
            ? extractErrorMessage(error.message) ?? error.message
            : undefined;

        mutationToastInfo.onError({
          errorMsg: message,
          onErrorToastOpts: {
            description: 'One-click V2 close failed, please try again',
          },
        });

        throw error;
      } finally {
        mutationToastInfo.onSettled();
        setIsAwaitingOneClickCloseResult(false);
      }
    },
    [
      addClosingPosition,
      addClosingTrade,
      canUseOneClickTradingV2,
      chainId,
      closeQty,
      invalidatePositionQuery,
      mutationToastInfo,
      onSuccess,
      position.id,
      position.timestamp,
      sendClosePositionV2,
    ],
  );

  const handleOneClickPartialCloseV2 = useCallback(
    async (tradeHash: Address) => {
      if (!canUseOneClickTradingV2 || !closeQty) return;

      setIsAwaitingOneClickCloseResult(true);
      mutationToastInfo.onMutate({
        title: 'In Progress',
        description: '⚡️ One-click V2 partial close in progress',
      });

      try {
        const intentHash = await sendPartialCloseV2(
          tradeHash,
          closeQty,
          broker,
        );

        const tradingStatus = await pollV2IntentStatus(intentHash);
        if (!tradingStatus.success) {
          throw new Error(
            tradingStatus.reason || 'One-click V2 partial close execution failed',
          );
        }

        invalidatePositionQuery();

        mutationToastInfo.onSuccess({
          title: '⚡️ Completed',
          description: 'Position partially closed successfully via one-click V2',
          txHash: tradingStatus.txnHash as Address,
        });

        onSuccess?.();

        return tradingStatus.txnHash;
      } catch (error) {
        const message =
          error instanceof Error
            ? extractErrorMessage(error.message) ?? error.message
            : undefined;

        mutationToastInfo.onError({
          errorMsg: message,
          onErrorToastOpts: {
            description: 'One-click V2 partial close failed, please try again',
          },
        });

        throw error;
      } finally {
        mutationToastInfo.onSettled();
        setIsAwaitingOneClickCloseResult(false);
      }
    },
    [
      broker,
      canUseOneClickTradingV2,
      closeQty,
      invalidatePositionQuery,
      mutationToastInfo,
      onSuccess,
      sendPartialCloseV2,
    ],
  );

  const closeTrade = useCallback(
    (tradeHash: Address) => {
      // OneClick V2 path (priority)
      if (canUseOneClickTradingV2) {
        // Partial close via OneClick V2
        if (closeQty && closeQty > 0n) {
          return handleOneClickPartialCloseV2(tradeHash);
        }
        // Full close via OneClick V2
        return handleOneClickCloseV2(tradeHash);
      }

      const address = getContractAddress('LeverUp', chainId);
      if (!address) {
        throw new Error('leverup contract address not found');
      }

      return writeContractOnchain({
        abi: TradingPortalFacet,
        address,
        functionName: 'closeTrade',
        args: !position.isMergedPosition
          ? [tradeHash]
          : closeQty
            ? [tradeHash, closeQty, broker]
            : [tradeHash, broker],
      });
    },
    [
      broker,
      canUseOneClickTradingV2,
      chainId,
      closeQty,
      handleOneClickCloseV2,
      position.isMergedPosition,
      writeContractOnchain,
    ],
  );

  const isClosePending =
    isPending || isSubmittingCloseV2 || isSubmittingPartialCloseV2 || isAwaitingOneClickCloseResult;

  return useMemo(
    () => ({
      isPending: isClosePending,
      writeContract: closeTrade,
    }),
    [closeTrade, isClosePending],
  );
};
